import * as React from 'react';
import { ConfirmDialog, type ConfirmDialogProps } from '@/components/app/ConfirmDialog';

type OpenOptions = Omit<ConfirmDialogProps, 'open' | 'onOpenChange' | 'onConfirm'> & {
  onConfirm: (args: { reason: string }) => Promise<void> | void;
};

interface ConfirmState extends OpenOptions {
  id: number;
  open: boolean;
  submitting: boolean;
}

interface ConfirmContextValue {
  confirm: (options: OpenOptions) => void;
}

const ConfirmContext = React.createContext<ConfirmContextValue | undefined>(undefined);

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = React.useState<ConfirmState | null>(null);

  const confirm = React.useCallback((options: OpenOptions) => {
    setState({ ...options, id: Date.now(), open: true, submitting: false });
  }, []);

  const close = React.useCallback(() => {
    setState((prev) => (prev ? { ...prev, open: false } : null));
  }, []);

  const handleConfirm = React.useCallback(
    async ({ reason }: { reason: string }) => {
      if (!state) return;
      setState((prev) => (prev ? { ...prev, submitting: true } : null));
      try {
        await state.onConfirm({ reason });
        setState((prev) => (prev ? { ...prev, open: false, submitting: false } : null));
      } catch (err) {
        setState((prev) => (prev ? { ...prev, submitting: false } : null));
        throw err;
      }
    },
    [state],
  );

  const value = React.useMemo(() => ({ confirm }), [confirm]);

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      {state && (
        <ConfirmDialog
          open={state.open}
          onOpenChange={(open) => (open ? null : close())}
          title={state.title}
          description={state.description}
          body={state.body}
          confirmLabel={state.confirmLabel}
          cancelLabel={state.cancelLabel}
          destructive={state.destructive}
          typedConfirmation={state.typedConfirmation}
          reasonRequired={state.reasonRequired}
          reasonPlaceholder={state.reasonPlaceholder}
          submitting={state.submitting}
          onConfirm={handleConfirm}
        />
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirmDialog() {
  const ctx = React.useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirmDialog must be used inside <ConfirmProvider>');
  return ctx.confirm;
}
